import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categoryMap, categories } from '@/mocks/categories';
import { articlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const catArticles = slug ? articlesByCategory[slug] || [] : [];
  const [activeTab, setActiveTab] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-500">分类未找到</p>
      </div>
    );
  }

  const categoryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <div className="min-h-screen pt-16">
      <StructuredData data={categoryStructuredData} />

      <div className="bg-background-100/70 border-b border-background-200/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-16">
          <Breadcrumb items={[{ label: category.name }]} />
          <div className="flex items-start gap-5">
            <div className={`w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-2xl ${category.colorClass} flex-shrink-0`}>
              <i className={`${category.icon} text-2xl md:text-3xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-[700px] leading-relaxed">
                {category.description}
              </p>
              <p className="text-xs text-foreground-400 mt-2">
                共收录 <strong className="text-foreground-700">{category.resourceCount}</strong> 个资源 · <strong className="text-foreground-700">{catArticles.length}</strong> 篇文章
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="border-b border-background-200/70 overflow-x-auto">
          <div className="flex items-center gap-1 py-3 min-w-max">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveTab(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="py-10 md:py-14">
          {catArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {catArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                    {article.featured && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                          精选
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <span>{article.publishedAt}</span>
                      <span>·</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}